import { Plus } from "@untitledui/icons"
import type { Key } from "react"
import { useEffect, useState } from "react"
import type { FieldError } from "react-hook-form"

import { CloseButton } from "@/components/ui/close-button"
import { Heading } from "@/components/ui/heading"
import { Dialog, Modal, ModalOverlay } from "@/components/ui/modal"
import { Select } from "@/components/ui/select"
import type { SelectItemType, SelectProps } from "@/components/ui/select"

import useContactStore from "../../../stores/useContactStore"
import type { ContactRoleFields, SchoolContactListItem } from "../../../types"

import NewSchoolContact from "./new_school_contact"

const ROLE_LABELS: Record<string, string> = {
  principal: "Principal",
  wellness_coordinator: "Wellness Coordinator",
  supervising_teacher: "Supervising Teacher",
  administrative: "Administrative",
  after_hours: "After-hours",
  other: "Other",
}

const formatRoles = (roles: ContactRoleFields[]): string => {
  if (!roles || roles.length === 0) return ""
  return roles
    .map((r) =>
      r.role_type === "other"
        ? r.detail
        : (ROLE_LABELS[r.role_type] ?? r.role_type),
    )
    .join(", ")
}

const contactToSelectItem = (c: SchoolContactListItem): SelectItemType => ({
  id: String(c.id),
  label: `${c.first_name} ${c.last_name}`,
  supportingText: formatRoles(c.contact_roles) || undefined,
  description:
    [c.email, c.telephone_number].filter(Boolean).join(" · ") || undefined,
})

interface SchoolContactProp extends Omit<
  SelectProps,
  "onChange" | "children" | "error"
> {
  accountId: number
  onChange?: (e: string) => void
  onSelect?: (contact: SchoolContactListItem) => void
  isDisabled?: boolean
  value?: string
  customData?: SchoolContactListItem[]
  error?: FieldError
}

const SchoolContact: React.FC<SchoolContactProp> = ({
  onChange,
  accountId,
  isDisabled = false,
  value,
  onSelect,
  customData,
  error,
  ...props
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [contactId, setContactId] = useState<string | undefined>(value)
  const schoolContacts = useContactStore((s) => s.contacts)
  const addSchoolContact = useContactStore((s) => s.add)
  const getSchoolContactDetails = useContactStore((s) => s.getById)

  useEffect(() => {
    setContactId(value)
  }, [value])

  const items: SelectItemType[] = [
    ...(customData ?? schoolContacts ?? []).map(contactToSelectItem),
    { id: "__new__", label: "New Contact", icon: Plus },
  ]

  const handleSelectionChange = (key: Key | null) => {
    if (!key) return
    if (key === "__new__") {
      setIsModalOpen(true)
      return
    }
    const id = String(key)
    setContactId(id)
    const contactDetails = getSchoolContactDetails(id)

    if (contactDetails) onSelect?.(contactDetails)
    onChange?.(id)
  }

  const handleCreateSuccess = async (newId: string) => {
    const res = await fetch(`/forms/school_contacts/${newId}`, {
      headers: { Accept: "application/json" },
    })
    if (res.ok) {
      const data = (await res.json()) as SchoolContactListItem
      addSchoolContact(data)
      setContactId(newId)
      onSelect?.(data)
      onChange?.(newId)
    }
    setIsModalOpen(false)
  }

  return (
    <>
      <Select
        placeholder="Select a contact"
        items={items}
        selectedKey={contactId ?? null}
        onSelectionChange={handleSelectionChange}
        isDisabled={isDisabled}
        error={error}
        {...props}
      >
        {(item) => (
          <Select.Item
            id={item.id}
            supportingText={item.supportingText}
            description={item.description}
            icon={item.icon}
          >
            {item.label}
          </Select.Item>
        )}
      </Select>
      <ModalOverlay
        isDismissable
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
      >
        <Modal className="sm:max-w-100">
          <Dialog className="overflow-hidden">
            <div className="bg-primary relative w-full overflow-hidden rounded-2xl shadow-xl">
              <CloseButton
                onClick={() => setIsModalOpen(false)}
                theme="light"
                size="lg"
                className="absolute top-3 right-3 z-10"
              />
              <div className="flex flex-col gap-0.5 px-4 pt-5 sm:px-6 sm:pt-6">
                <Heading
                  slot="title"
                  className="text-md text-primary font-semibold"
                >
                  New contact
                </Heading>
                <p className="text-tertiary text-sm">
                  Add a new contact for this school.
                </p>
              </div>
              <NewSchoolContact
                accountId={accountId}
                onSuccess={handleCreateSuccess}
                onCancel={() => setIsModalOpen(false)}
              />
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  )
}

export default SchoolContact
